import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import GenericCasinoTimer from "../../components/GenericCasinoTimer";
import { normalizeHeadsTailsResults, winnerOf } from "./headsTailsResults.js";
import { GetUniversalLastTenResult, getUniversalCasinoPlExposureByRunner } from "./genericFunctions";
import { getUniversalCasinoOddsAPI, getUniversalCasinoLastResultAPI, getCasinoExposureAPI, uniCasinoBetAPI } from "../../service/casino";
import "./HeadsAndTails.css";
import { getHeadsTailsMarketState, isHeadsTailsRunnerActive, isHeadsTailsTableOnline, normalizeHeadsTailsMarkets } from "./headsTailsMarket.js";

const GAME_ID = "UNI_HEADS_AND_TAILS";
const stakes = [
  { img: "chips1", amount: 100, label: "100" },
  { img: "chips1", amount: 500, label: "500" },
  { img: "chips1k", amount: 1000, label: "1k" },
  { img: "chips5", amount: 5000, label: "5k" },
  { img: "chips10", amount: 10000, label: "10k" },
  { img: "chips20", amount: 25000, label: "25k" },
  { img: "chips1k", amount: 50000, label: "50k" },
  { img: "chips1k", amount: 100000, label: "1L" },
];


export default function HeadsAndTails() {
  const [odds, setOdds] = useState(null);
  const [results, setResults] = useState([]);
  const [resultsError, setResultsError] = useState(false);
  const [exposure, setExposure] = useState([]);
  const [feedError, setFeedError] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [payload, setPayload] = useState(null);
  const [stake, setStake] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const latest = useRef(null);
  const busy = useRef(false);
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    let active = true;
    const timers = new Set();
    // Schedule after completion to avoid overlapping requests and stale responses.
    const poll = async (request, delay) => {
      await request();
      if (active) {
        const timer = setTimeout(() => { timers.delete(timer); poll(request, delay); }, delay);
        timers.add(timer);
      }
    };
    poll(async () => {
      try {
        const data = await getUniversalCasinoOddsAPI(GAME_ID);
        if (!active) return;
        if (data?.code !== 200 || !data.response?.roundId) throw new Error();
        const next = { ...data.response, markets: normalizeHeadsTailsMarkets(data.response.markets), deadline: Date.now() + Math.max(0, Number(data.response.seconds) || 0) * 1000 };
        latest.current = next;
        setOdds(next);
        setFeedError(false);
        setPayload((previous) => previous && previous.marketId !== next.roundId ? null : previous);
      } catch {
        if (active) { latest.current = null; setFeedError(true); }
      }
    }, 800);
    poll(async () => {
      try {
        const data = await getUniversalCasinoLastResultAPI(GAME_ID);
        if (!active) return;
        if (data?.code !== 200) throw new Error();
        setResults(normalizeHeadsTailsResults(data.response));
        setResultsError(false);
      } catch { if (active) setResultsError(true); }
    }, 1500);
    poll(async () => {
      const roundId = latest.current?.roundId;
      if (!roundId) return;
      try {
        const data = await getCasinoExposureAPI(roundId);
        if (active && latest.current?.roundId === roundId) setExposure(Array.isArray(data?.response) ? data.response.filter((item) => item && Array.isArray(item.casinoPLExposure)).map((item) => ({ ...item, casinoPLExposure: item.casinoPLExposure.filter((entry) => entry && typeof entry.selectionName === "string") })) : []);
      } catch { if (active) setExposure([]); }
    }, 2000);
    const clock = setInterval(() => setNow(Date.now()), 250);
    return () => { active = false; mounted.current = false; timers.forEach(clearTimeout); clearInterval(clock); };
  }, []);

  useEffect(() => { setExposure([]); }, [odds?.roundId]);
  const marketState = getHeadsTailsMarketState(odds, now);
  const open = !feedError && marketState.open;
  const canSelectOdds = !feedError && isHeadsTailsTableOnline(odds);
  const markets = Array.isArray(odds?.markets) ? odds.markets : [];
  const latestResult = results[0];
  const latestWinner = winnerOf(latestResult);
  const latestWinnerIsTails = latestWinner === "TAILS" || latestWinner === "T";
  const coinResultKey = `${latestResult?.roundId || "waiting"}-${latestWinner || "HEADS"}`;

  const placeBet = async (payload) => {
    if (busy.current || !payload) return;
    if (!payload.amount) { toast.error("Please select amount chips"); return; }
    const current = latest.current;
    const market = current?.markets?.find((item) => item.oddDetailsDTO?.some((runner) => `${item.marketId}_${runner.selectionId}` === payload.marketIdWithSelectionId));
    const runner = market?.oddDetailsDTO?.find((item) => `${market.marketId}_${item.selectionId}` === payload.marketIdWithSelectionId);
    if (!current) {
      toast.error("Live odds unavailable. Please wait for the next update."); return;
    }
    if (!isHeadsTailsTableOnline(current)) {
      return;
    }
    if (String(current.roundId) !== String(payload.marketId)) {
      return;
    }
    if (!runner) {
      return;
    }
    if (!isHeadsTailsRunnerActive(runner)) {
      return;
    }
    if (Number(runner.back1) !== Number(payload.back)) {
      payload = { ...payload, back: runner.back1 };
      setPayload(payload);
    }
    const amount = Number(payload.amount);
    if (!Number.isFinite(amount) || amount <= 0 || amount < Number(market.min) || amount > Number(market.max)) {
      toast.error(`Enter a stake between ${market.min} and ${market.max}.`); return;
    }
    setPayload(payload);
    busy.current = true;
    setSubmitting(true);
    try {
      const data = await uniCasinoBetAPI({ ...payload, amount });
      if (!mounted.current) return;
      if (data?.code === 200) { toast.success("Bet placed successfully"); }
      else toast.error(typeof data?.response === "string" ? data.response : "Bet could not be placed. Check your bets before trying again.");
    } catch { if (mounted.current) toast.error("Unable to confirm the bet. Check your bets before trying again."); }
    finally { busy.current = false; if (mounted.current) setSubmitting(false); }
  };

  const selectChip = (amount) => {
    if (busy.current) return;
    setStake(amount);
  };

  const selectOdds = (market, runner) => {
    if (busy.current || !canSelectOdds) return;
    const selection = {
      amount: stake,
      back: runner.back1,
      casinoGames: GAME_ID,
      marketId: odds.roundId,
      marketIdWithSelectionId: `${market.marketId}_${runner.selectionId}`,
      runnerName: runner.runnerName,
      type: "back",
    };
    setPayload(selection);
    if (stake > 0) placeBet(selection);
  };

  return (
    <div className="heads-tails-page">
      
      <div className="ht-coin-stage" aria-label={`Latest result: ${latestWinner || "waiting"}`}>
        <div className="ht-coin-scene">
          <div
            key={coinResultKey}
            className={`ht-live-coin ${latestWinner ? "ht-coin-animate" : ""}`}
            style={{
              "--ht-coin-final": latestWinnerIsTails ? "1980deg" : "1800deg",
            }}
          >
            <div className="ht-coin-face ht-coin-heads">
              <img
                src="https://casino.allinone247.com/assets/img/heads.png"
                alt="Heads"
              />
            </div>
            <div className="ht-coin-face ht-coin-tails">
              <img
                src="https://casino.allinone247.com/assets/img/tails.png"
                alt="Tails"
              />
            </div>
          </div>
        </div>
        <div className="ht-timer"><GenericCasinoTimer time={odds?.seconds} status={open ? "ONLINE" : "SUSPEND"} black /></div>
      </div>
      <div className={feedError ? "ht-status" : "ht-accessible-status"} role="status">{feedError ? "Live odds unavailable. Reconnecting…" : marketState.message}</div>
      
      {markets.map((market) => (
        <section key={market.marketId} className="ht-market">
          <div className="ht-sides">
            {(market.oddDetailsDTO || []).map((runner) => {
              const selection = `${market.marketId}_${runner.selectionId}`;
              const locked = !canSelectOdds;
              return <div className="ht-runner" key={runner.selectionId}>
                <span className="ht-exposure">
                  {getUniversalCasinoPlExposureByRunner(
                    exposure,
                    runner.runnerName,
                    runner.selectionId
                  )}
                </span>
                <button type="button" className={`ht-side ${runner.runnerName === "HEADS" ? "ht-heads" : "ht-tails"} ${payload?.marketIdWithSelectionId === selection ? "ht-selected" : ""}`} disabled={locked} aria-label={`${runner.runnerName}${locked ? " — betting locked" : ` — ${runner.back1}`}`} onClick={() => selectOdds(market, runner)}>
                <img className="ht-bet-image" src={`https://casino.royall444.com/assets/img/${runner.runnerName === "HEADS" ? "head" : "tails"}-bet.jpg`} alt={runner.runnerName} />
                <span className="ht-price">{Number.isFinite(Number(runner.back1)) ? Number(runner.back1).toFixed(2) : "—"}</span>
                {locked && <span className="ht-lock-overlay" aria-label="Betting locked"><i className="fa fa-lock" aria-hidden="true" /></span>}
                </button>
              </div>;
            })}
          </div>
        </section>
      ))}
      <section _ngcontent-ng-c1910803830="" id="functionZone" className="function_zone" aria-label="Select stake">
        <ul _ngcontent-ng-c1910803830="" id="chips" className="chips">
          {stakes.map((item) => (
            <li key={item.amount} _ngcontent-ng-c1910803830=""
              className={`chips_1 ${stake === item.amount ? "now" : ""}`}
              role="button" tabIndex={submitting ? -1 : 0}
              aria-pressed={stake === item.amount} aria-disabled={submitting}
              aria-label={`Select stake ${item.amount}`}
              onClick={() => selectChip(item.amount)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  selectChip(item.amount);
                }
              }}>
              <img _ngcontent-ng-c1910803830="" src={`https://casino.queenexch.com/assets/chips/${item.img}.svg`} alt="" />
              <p _ngcontent-ng-c1910803830="">{item.label} </p>
              <span _ngcontent-ng-c1910803830="" className="ring">ring</span>
            </li>
          ))}
        </ul>
      </section>

      {resultsError && <p role="status">Results unavailable. Reconnecting…</p>}
      <GetUniversalLastTenResult casinoGames={GAME_ID} lastResult={{ data: results.map((result) => ({ ...result, winner: winnerOf(result), eventName: "HEADS & TAILS" })) }} />
      {!results.length && !resultsError && <p>No recent results.</p>}
    </div>
  );
}
